import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Venus, Droplets, Gauge, Scale, Activity, HeartPulse, Sparkles, ArrowRight, ArrowLeft,
  Leaf, Salad, Egg, Drumstick, Target, BatteryCharging, Moon, CheckCircle2,
} from "lucide-react";

const CONDITIONS = [
  { v: "pcos", l: "PCOS / PCOD", icon: Venus, preset: "pcos", cond: "PCOS", teaser: "Higher-protein, lower-GI meals that support insulin sensitivity." },
  { v: "t2d", l: "Type 2 Diabetes", icon: Droplets, preset: "diabetes", cond: "diabetes", teaser: "Blood-sugar-conscious, lower-GI plates with smart swaps." },
  { v: "prediabetes", l: "Prediabetes", icon: Gauge, preset: "diabetes", cond: "prediabetes", teaser: "Preventive, balanced meals to keep glucose steady." },
  { v: "obesity", l: "Weight / Obesity", icon: Scale, preset: "lose", cond: "", teaser: "Sustainable, satisfying meals for steady, healthy fat loss." },
  { v: "fatty_liver", l: "Fatty Liver (NAFLD)", icon: Activity, preset: "maintain", cond: "fatty liver", teaser: "Higher-fiber, lower saturated-fat, liver-supportive meals." },
  { v: "metabolic", l: "Metabolic Syndrome", icon: HeartPulse, preset: "cardiac", cond: "metabolic syndrome", teaser: "Whole-pattern meals for blood sugar, lipids & weight." },
  { v: "wellness", l: "General wellness", icon: Leaf, preset: "maintain", cond: "", teaser: "Balanced, personalized everyday nutrition for how you live." },
];
const GOALS = [
  { v: "lose", l: "Lose weight", icon: Scale },
  { v: "sugar", l: "Balance blood sugar", icon: Droplets },
  { v: "energy", l: "More energy", icon: BatteryCharging },
  { v: "fitness", l: "Get fitter / leaner", icon: Target },
  { v: "sleep", l: "Sleep & recovery", icon: Moon },
];
const DIETS = [
  { v: "veg", l: "Vegetarian", icon: Salad },
  { v: "non_veg", l: "Non-vegetarian", icon: Drumstick },
  { v: "eggitarian", l: "Eggitarian", icon: Egg },
];

const stepVariants = {
  enter: { opacity: 0, y: 16 },
  center: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
};

export default function LandingQuiz({ onStart }) {
  const [step, setStep] = useState(0);
  const [ans, setAns] = useState({ condition: "", goal: "", diet: "" });

  const condObj = CONDITIONS.find((c) => c.v === ans.condition);

  const choose = (key, value, last = false) => {
    const next = { ...ans, [key]: value };
    setAns(next);
    if (last) {
      // Persist so onboarding can prefill the user's context after sign-in.
      try {
        const co = CONDITIONS.find((c) => c.v === next.condition);
        localStorage.setItem("tuha_quiz", JSON.stringify({
          condition: next.condition, goal: next.goal, diet: next.diet,
          preset: co?.preset || "", cond_label: co?.cond || "",
        }));
      } catch (_) {}
      setStep(3);
    } else {
      setStep((s) => s + 1);
    }
  };

  const Q = ({ title, options, k, last }) => (
    <div>
      <h3 className="font-serif-display text-2xl md:text-3xl text-foreground text-center">{title}</h3>
      <div className="mt-6 flex flex-wrap justify-center gap-2.5">
        {options.map(({ v, l, icon: Icon }) => (
          <button
            key={v}
            onClick={() => choose(k, v, last)}
            data-testid={`quiz-${k}-${v}`}
            className="inline-flex items-center gap-2 rounded-full border border-border bg-surface-elevated px-5 py-3 text-sm md:text-base font-medium text-foreground/85 hover:border-primary hover:bg-primary/5 hover:-translate-y-0.5 active:scale-95 transition-all"
          >
            <Icon size={16} className="text-primary" /> {l}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <section className="max-w-3xl mx-auto px-6 md:px-12 py-6 md:py-10" id="quiz">
      <div className="relative rounded-[2rem] border border-border bg-surface p-7 md:p-10 shadow-sm overflow-hidden" data-testid="landing-quiz">
        <div className="absolute -right-10 -top-10 w-44 h-44 rounded-full bg-secondary/10 blur-3xl -z-0"></div>
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-secondary">
              <Sparkles size={13} /> 60-second check-in
            </span>
            <div className="flex gap-1.5" aria-hidden>
              {[0, 1, 2, 3].map((i) => (
                <span key={i} className={`h-1.5 rounded-full transition-all ${i <= step ? "w-6 bg-primary" : "w-3 bg-border"}`} />
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={step} variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.28, ease: "easeOut" }}>
              {step === 0 && (
                <Q title="What would you like KAAYA to help with?" k="condition" options={CONDITIONS} />
              )}
              {step === 1 && (
                <Q title="What's your main goal right now?" k="goal" options={GOALS} />
              )}
              {step === 2 && (
                <Q title="How do you prefer to eat?" k="diet" options={DIETS} last />
              )}
              {step === 3 && (
                <div className="text-center" data-testid="quiz-result">
                  <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                    <CheckCircle2 size={26} className="text-primary" />
                  </div>
                  <h3 className="font-serif-display text-2xl md:text-4xl text-primary tracking-tight">
                    Your personalized plan is ready.
                  </h3>
                  <p className="mt-3 text-foreground/75 max-w-md mx-auto leading-relaxed">
                    {condObj?.teaser || "Personalized, evidence-informed nutrition tailored to you."}
                    {" "}KAAYA will fine-tune it to your biology, preferences and local kitchen.
                  </p>
                  <div className="mt-6 flex flex-col items-center gap-3">
                    <Button onClick={onStart} data-testid="quiz-cta" size="lg" className="rounded-full px-8 h-12 bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20">
                      Create my plan — Continue with Google <ArrowRight size={18} className="ml-2" />
                    </Button>
                    <button onClick={() => { setStep(0); setAns({ condition: "", goal: "", diet: "" }); }} data-testid="quiz-restart" className="text-xs text-muted-foreground hover:text-foreground transition">
                      Start over
                    </button>
                  </div>
                  <p className="mt-5 text-[11px] text-muted-foreground">Evidence-informed guidance — not a substitute for medical care.</p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {step > 0 && step < 3 && (
            <div className="mt-7 flex justify-center">
              <button onClick={() => setStep((s) => Math.max(0, s - 1))} data-testid="quiz-back" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition">
                <ArrowLeft size={14} /> Back
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
